
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

export default function HomePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // Agregar Schema.org JSON-LD para la página de inicio
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Agenda Padres Bebés - Inicio",
      "description": "Página principal de SOS Padres, la aplicación completa para el cuidado de bebés",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://app.sospadres.info'}/`,
      "mainEntity": {
        "@type": "SoftwareApplication",
        "name": "Agenda Padres Bebés",
        "applicationCategory": "HealthApplication",
        "description": "Aplicación móvil para el seguimiento completo del cuidado de bebés: alimentación, sueño, pañales, crecimiento y desarrollo.",
        "operatingSystem": "Web",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
        // Acá había un `aggregateRating` de "4.8 estrellas con 150
        // valoraciones". Nunca existieron: venía inventado de la
        // plantilla. Se sacó por dos motivos: no es cierto, y Google
        // penaliza las valoraciones falsas — puede quitarte los
        // resultados enriquecidos de todo el dominio, no solo de esta
        // página. Cuando haya valoraciones reales, vuelve acá con el
        // número real.
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://app.sospadres.info'}/`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Solo redirigir si el usuario viene directamente y ya está autenticado
  // useEffect(() => {
  //   if (!loading && user) {
  //     navigate('/dashboard', { replace: true });
  //   }
  // }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-center">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <div className="px-4 pt-12 pb-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
            Agenda Padres Bebés
          </h1>
          <p className="text-lg text-gray-600 mb-6">
            La app de SOS Padres para el día a día de tu bebé
          </p>
        </div>

        {/* Hero Image */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-xl">
          <img
            src="/img/hero-familia.jpg"
            alt="Familia feliz con bebé - SOS Padres app para el cuidado infantil"
            className="w-full h-48 object-cover object-top"
          />
        </div>

        {/* Action Buttons */}
        <div className="space-y-4 mb-8">
          {!user ? (
            <>
              <button
                onClick={() => navigate('/auth?mode=register')}
                className="w-full bg-blue-600 text-white py-4 rounded-2xl font-semibold text-lg shadow-lg hover:bg-blue-7 transition-colors"
              >
                <i className="ri-user-add-line mr-2"></i>
                Crear Cuenta Nueva
              </button>
              <button
                onClick={() => navigate('/auth?mode=login')}
                className="w-full bg-white text-blue-600 py-4 rounded-2xl font-semibold text-lg shadow-lg border-2 border-blue-600 hover:bg-blue-50 transition-colors"
              >
                <i className="ri-login-box-line mr-2"></i>
                Ya tengo cuenta
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-green-600 text-white py-4 rounded-2xl font-semibold text-lg shadow-lg hover:bg-green-700 transition-colors"
            >
              <i className="ri-dashboard-line mr-2"></i>
              Ir al Dashboard
            </button>
          )}
        </div>
      </div>

      {/* Features Section */}
      <div className="px-4 pb-8">
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">
          Todo lo que necesitas para cuidar a tu bebé
        </h2>
        
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl overflow-hidden">
              <img
                src="/img/icono-alimentacion.jpg"
                alt="Registro de alimentación del bebé"
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">Alimentación</h3>
            <p className="text-xs text-gray-600 mt-1">Registro de horarios</p>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl overflow-hidden">
              <img
                src="/img/icono-sueno.jpg"
                alt="Control de sueño del bebé"
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">Sueño</h3>
            <p className="text-xs text-gray-600 mt-1">Control de descanso</p>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl overflow-hidden">
              <img
                src="/img/icono-panales.jpg"
                alt="Seguimiento de cambios de pañales"
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">Pañales</h3>
            <p className="text-xs text-gray-600 mt-1">Seguimiento de cambios</p>
          </div>

          <div className="bg-white rounded-2xl p-4 shadow-lg text-center">
            <div className="w-16 h-16 mx-auto mb-3 rounded-2xl overflow-hidden">
              <img
                src="/img/icono-crecimiento.jpg"
                alt="Monitoreo de crecimiento y desarrollo"
                className="w-full h-full object-cover"
              />
            </div>
            <h3 className="font-semibold text-gray-800 text-sm">Crecimiento</h3>
            <p className="text-xs text-gray-600 mt-1">Monitoreo desarrollo</p>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-white rounded-2xl p-6 shadow-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
            ¿Por qué elegir SOS Padres?
          </h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 text-lg mr-3"></i>
              <span className="text-gray-700 text-sm">Registro fácil y rápido</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 text-lg mr-3"></i>
              <span className="text-gray-700 text-sm">Estadísticas detalladas</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 text-lg mr-3"></i>
              <span className="text-gray-700 text-sm">Múltiples bebés</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 text-lg mr-3"></i>
              <span className="text-gray-700 text-sm">Historial completo</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 text-lg mr-3"></i>
              <span className="text-gray-700 text-sm">Calendario de vacunaciones</span>
            </div>
            <div className="flex items-center">
              <i className="ri-check-line text-green-500 text-lg mr-3"></i>
              <span className="text-gray-700 text-sm">Seguimiento de hitos del desarrollo</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
